import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { bookingsService } from '@/api/bookings.service';
import { useMutation } from '@tanstack/vue-query';
import type { User } from '@/entities/auth/user.entity';
import type { BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import type {
  BookingPricingDto,
  TaxProcedure,
  UpdateCustomBookedExtraDto,
} from '@carvia/ros-client-types';
import { bookingLocationToCreateFields } from '@/utils/booking-location-to-location-dto';

export const useCreatePricingForBookingProcess = (props: CreateBookingProps) =>
  useMutation({
    mutationKey: [
      'create-pricing-for-booking-process',
      props.selectedOffer.value?.car.id,
      props.startDate.value,
      props.endDate.value,
      props.longTerm.value,
    ],
    mutationFn: async ({
      bookedExtras,
      agency,
      customBookedExtras,
      taxProcedure,
    }: {
      bookedExtras: BookedExtraSelection[];
      agency?: User | null;
      customBookedExtras: UpdateCustomBookedExtraDto[] | undefined;
      taxProcedure: TaxProcedure;
    }) => {
      if (!props.selectedOffer.value || !props.pickupLocation.value || !props.dropoffLocation.value)
        return;
      const booking = await bookingsService.create({
        dryRun: true,
        bookedCarId: props.selectedOffer.value.car.id,
        additionalDrivers: [],
        startDate: props.startDate.value,
        endDate: props.endDate.value,
        pricing: {
          bookedExtras,
          customBookedExtras,
          ...(props.longTerm.value ? { type: 'LONG_TERM' as const } : {}),
        } as BookingPricingDto,
        ...bookingLocationToCreateFields('pickup', props.pickupLocation.value),
        ...bookingLocationToCreateFields('dropoff', props.dropoffLocation.value),
        agencyId: agency?.id,
        insuranceCaseId: props.insuranceCase.value?.id,
        taxProcedure,
      });
      return booking.priceCalculation;
    },
    onMutate: () => ({
      showSuccessToast: false,
      invalidateQueryKeys: null,
    }),
  });
